import AppointmentDatetimePicker from "components/appointment-datetime-picker";
import PrivateLaboratoryBanner from "components/private-laboratory-banner";
import Appointment from "models/appointment";
import React, { CSSProperties } from "react";
import { ChevronDown, ChevronUp, Icon } from "react-feather";
import { CSSTransition } from "react-transition-group";
import useLanguage from "../../../hooks/language";
import Clinic, { ClinicLaboratoryModel } from "../../../models/clinic";
import NextAvailableTag, {
    ResultsIn48HoursTag,
} from "../next-available-tag/index";
import styles from "./clinic-card.module.css";

export default function ClinicCard({
    clinic,
    selectedClinicID,
    setSelectedClinicID,
    style,
    appointment,
    onDateSelect,
    startDate,
    UnSelectedStateIcon = ChevronDown,
    containerRef,
}: {
    clinic: Clinic;
    selectedClinicID: number | null;
    setSelectedClinicID: (id: number | null) => void;
    style?: CSSProperties;
    appointment?: Appointment;
    onDateSelect?: (datetime: string | null) => void;
    startDate?: string;
    UnSelectedStateIcon?: Icon;
    containerRef?: React.Ref<HTMLDivElement>;
}): React.ReactElement {
    const { translations, language } = useLanguage();

    const isPrivateLaboratory =
        clinic.laboratoryModel === ClinicLaboratoryModel.Private;

    return (
        <div className={styles.scrollTarget} ref={containerRef} style={style}>
            <div
                className={styles.clinicCardContainer}
                onClick={() =>
                    clinic.id === selectedClinicID
                        ? setSelectedClinicID(null)
                        : setSelectedClinicID(clinic.id)
                }
            >
                <div className={styles.cardContainer}>
                    <h3 className={styles.cardContainerTitle}>{clinic.name}</h3>
                    <span className={styles.cardContainerAddress}>
                        {typeof clinic?.distanceInMeters === "number" && (
                            <>
                                <span className={styles.cardContainerDistance}>
                                    {(clinic.distanceInMeters / 1000).toFixed(
                                        1
                                    )}{" "}
                                    km •{" "}
                                </span>
                            </>
                        )}
                        <span className={styles.cardContainerCity}>
                            {clinic.city}
                        </span>
                        <span> - {clinic.street}</span>
                    </span>
                    {/* Clinic ID 9: Montreal - Saint Laurent ⚡ */}
                    {clinic.id === 9 ? (
                        <ResultsIn48HoursTag
                            text={
                                translations.clinicSelection.privateLaboratory
                                    .resultsIn48Hours
                            }
                        />
                    ) : (
                        clinic?.nextAvailableTime && (
                            <NextAvailableTag
                                language={language}
                                nextAvailableTime={clinic.nextAvailableTime}
                            />
                        )
                    )}
                </div>
                {clinic.id === selectedClinicID ? (
                    <ChevronUp />
                ) : (
                    <UnSelectedStateIcon />
                )}
            </div>

            <CSSTransition
                in={selectedClinicID === clinic.id}
                timeout={500}
                classNames={{
                    enter: styles.enter,
                    enterActive: styles.enterActive,
                    enterDone: styles.enterDone,
                    exit: styles.exit,
                    exitActive: styles.exitActive,
                }}
                unmountOnExit
                mountOnEnter
            >
                <div>
                    {isPrivateLaboratory && <PrivateLaboratoryBanner />}

                    <AppointmentDatetimePicker
                        clinicId={clinic.id}
                        appointment={appointment}
                        onSelectDate={onDateSelect}
                        startDate={startDate ?? undefined}
                    />

                    {isPrivateLaboratory && (
                        <p className={styles["feeDisclaimer__paragraph"]}>
                            {
                                translations.clinicSelection.privateLaboratory
                                    .disclaimer
                            }
                        </p>
                    )}
                </div>
            </CSSTransition>
        </div>
    );
}
